import {
  makeStyles,
  Button,
  Collapse,
  FilePreview,
  FormSectionTitle,
  Grid,
  Typography,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { FeatureProduct } from '@paintscout/util/builder'
import {
  applyProductLayoutOrder,
  getFeatureProductFile,
  getQuoteFeatureProducts,
  withoutHiddenProducts
} from '@paintscout/util/builder'
import classNames from 'classnames'
import { useFlags } from 'shared/components/common/FlagsProvider'
import React, { useContext, useState } from 'react'
import { QuoteContext } from '../../../../context/QuoteContext'
import ItemTable, { ItemTableCell } from '../../../../ItemTable'

const paintCanSize = 72
const maxProductImageHeight = 140

const useStyles = makeStyles<Theme, ProductsTableProps>(
  (theme) => ({
    root: {},
    tableHead: {
      background: theme.paintscout.palette.brand.Neutral.P94
    },
    headerCell: {
      color: theme.paintscout.palette.brand.Neutral.P35
    },
    filePreview: {
      maxWidth: paintCanSize,
      minWidth: paintCanSize,
      minHeight: paintCanSize,
      // Cap tall images so the box tops out at maxProductImageHeight (FilePreview adds 2px padding a side)
      '& img': {
        maxHeight: maxProductImageHeight - 4
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: paintCanSize,
        minWidth: paintCanSize,
        minHeight: paintCanSize,
        marginLeft: theme.spacing(-1)
      }
    },
    productDetails: {
      '&$productDetails': {
        paddingLeft: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
          paddingLeft: theme.spacing(1)
        }
      }
    },
    productLabel: {
      '&$productLabel': {
        color: theme.paintscout.palette.brand.Neutral.P35,
        marginBottom: theme.spacing(1)
      }
    },
    hiddenTableHead: {
      display: 'none'
    },
    dividerTop: {
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    viewMoreContainer: {
      marginTop: theme.spacing(2),
      textAlign: 'center'
    }
  }),
  { name: 'ProductsTable' }
)

export interface ProductsTableProps {
  classes?: StyleClasses<typeof useStyles>
  collapsed?: boolean
  inPresentation?: boolean
  onToggle: (ev: React.MouseEvent) => any
}

function ProductsTable(props: ProductsTableProps) {
  const classes = useStyles(props)
  const { collapsed, onToggle, inPresentation } = props
  const { quote, isEditable, isCustomerView } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const isXs = useMediaQuery('xs')
  // The order the estimator saved in the next editor (ENG-2240), which may mix SW and custom
  // products (ENG-2237); SW first by default.
  const { customProductVisuals } = useFlags()
  const quoteItems = applyProductLayoutOrder(
    quote,
    getQuoteFeatureProducts({ quote, options, includeCustomProducts: !!customProductVisuals })
  )
  // Products hidden in the next editor (ENG-2240) stay out of whatever the customer sees, even when
  // this classic document renders it. This view has no unhide control, so it only lists them in an
  // editable, non-customer-facing estimate, as the next editor does.
  const items = isEditable && !inPresentation && !isCustomerView ? quoteItems : withoutHiddenProducts(quote, quoteItems)
  // The header row carries the SW name and logo, so it only heads an all-SW list. Once a custom
  // product is listed (ENG-2237) the row goes and a top border divides the title from the rows.
  const showSWHeader = !items.some((item) => !item.featureProduct)
  const show = !collapsed && items.length > 0
  const hidePrint = items.length === 0 || collapsed

  const [showAll, setShowAll] = useState(false)
  const visibleItems = showAll ? items : items.slice(0, 3)

  const SherwinWilliamsLogo = isXs ? (
    <img
      src={'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/s-w-icon.png'}
      alt={'Sherwin-Williams Logo'}
      style={{ height: 36, position: 'absolute', top: 4, right: 15 }}
    />
  ) : (
    <img
      src={'https://res.cloudinary.com/taptapquote/image/upload/v1751660780/paintscout/s-w-logo.png'}
      alt={'Sherwin-Williams Logo'}
      style={{ height: 36, position: 'absolute', top: 6, right: 8 }}
    />
  )

  if (!show && inPresentation) {
    return null
  }

  return (
    <div className={classNames({ [classes.root]: true, [classes.hidePrint]: hidePrint })}>
      <FormSectionTitle title={'Products'} showToggle={!inPresentation} toggleValue={!collapsed} onToggle={onToggle} />
      <Collapse show={show}>
        <div className={classNames({ [classes.dividerTop]: !showSWHeader })}>
          <ItemTable
            draggable={false}
            classes={{
              tableHead: showSWHeader ? classes.tableHead : classes.hiddenTableHead
            }}
            items={visibleItems}
            checkboxes={false}
            showAddButton={false}
            onItemClick={null}
            renderHeadColumns={() => (
              <div style={{ position: 'relative' }}>
                <ItemTableCell className={classes.headerCell}>Sherwin-Williams</ItemTableCell>
                <ItemTableCell className={classes.headerCell}>{SherwinWilliamsLogo}</ItemTableCell>
              </div>
            )}
            renderRowColumns={(item: FeatureProduct) => {
              const file = getFeatureProductFile(item)
              return (
                <>
                  <ItemTableCell colSpan={2}>
                    <Grid container spacing={2}>
                      {file && (
                        <Grid item xs={12} sm={'auto'}>
                          <FilePreview
                            classes={{
                              root: classes.filePreview
                            }}
                            file={file}
                          />
                        </Grid>
                      )}
                      {/* The left padding spaces the text off the image, so it only applies when there is one */}
                      <Grid item xs={12} sm className={classNames({ [classes.productDetails]: !!file })}>
                        <Typography variant="h3" className={classes.productLabel}>
                          {item.label}
                        </Typography>
                        <Typography variant="body1">{item.description}</Typography>
                      </Grid>
                    </Grid>
                  </ItemTableCell>
                </>
              )
            }}
            {...props}
          />
        </div>

        {items.length > 3 && (
          <div className={classes.viewMoreContainer}>
            <Button variant={'text'} onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? 'View Less' : 'View More'}
            </Button>
          </div>
        )}
      </Collapse>
    </div>
  )
}

export default ProductsTable
